// ─── Public RSVP Page (dedicated) ───────────────────────────────
// Route: /invite/:slug/rsvp
//
// Purpose: A focused, distraction-free RSVP page reached by tapping an
// RSVP Button (or any element with action=open_rsvp_page) on the public
// invitation canvas. Reuses the existing RSVPForm submission logic so
// there's a single source of truth for RSVP behavior.
//
// Backward-compat: this is an ADDITIONAL entry point — the inline RSVP
// sections used today continue to render exactly the same on
// /invite/:slug.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import RSVPForm from '../components/RSVPTemplates/RSVPForm';
import RsvpSection from '../components/sections/rsvp/RsvpSection';
import LayoutDebugOverlay from '../components/public/LayoutDebugOverlay';
import PreviewDraftBadge from '../components/public/PreviewDraftBadge';
import NewVersionBanner from '../components/editor/NewVersionBanner';
import { purgeStaleServiceWorkers } from '../lib/utils/purgeStaleServiceWorkers';
import { buildPageLayoutCss } from '../lib/utils/rsvpPageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicInviteRsvp() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Same draft/preview mechanism as PublicInvite.js — the backend
  // /api/public/invite/{slug} endpoint accepts ?preview=TOKEN and
  // bypasses the "is_published" gate when the token matches the
  // rsvp_page's preview_token. Without this, designers tapping the
  // RSVP Button from a Share Preview link saw "Invitation not yet
  // published".
  const previewToken = searchParams.get('preview');
  const queryString = searchParams.toString();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // See PublicInvite for rationale: native fetch with
        // cache:'reload' is the strongest no-cache signal we can
        // send. Bypasses HTTP cache and Service Worker response cache.
        const baseUrl = `${API}/api/public/invite/${slug}`;
        const url = previewToken
          ? `${baseUrl}?preview=${encodeURIComponent(previewToken)}&_ts=${Date.now()}`
          : baseUrl;
        try { window.__sitoaLastFetchUrl = url; } catch { /* noop */ }
        let payload;
        if (previewToken) {
          const r = await fetch(url, {
            cache: 'reload',
            credentials: 'omit',
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          payload = await r.json();
        } else {
          const res = await axios.get(url);
          payload = res.data;
        }
        if (!cancelled) {
          setData(payload);
          try { window.__sitoaResponseIdentity = payload?.response_identity || null; } catch { /* noop */ }
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Invitation not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, previewToken]);

  // Stale SW / cache rescue — see PublicInvite for rationale.
  useEffect(() => { purgeStaleServiceWorkers(); }, []);

  const event = data?.event || {};
  const rsvpPage = data?.rsvp_page || {};
  const dedicatedSection = data?.dedicated_rsvp_section || null;
  // Reuse the RSVP page's color theme / fonts so the dedicated screen
  // feels like a continuation of the invitation (no jarring whitebox).
  const colorTheme = rsvpPage.color_theme || {};
  const bg = colorTheme.background || '#f7f4ef';
  const fg = colorTheme.text_primary || '#1a1a1a';
  const accent = colorTheme.accent || '#1a1a1a';
  const headingFont = rsvpPage.font_heading || 'serif';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bg, color: fg }}>
        <Loader2 className="w-6 h-6 animate-spin opacity-60" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: bg, color: fg }}>
        <p className="text-base opacity-80">{error}</p>
      </div>
    );
  }

  // Build the scoped CSS for the page-layout overrides (edge-to-edge
  // and top/bottom spacing). Returns the wrapper class + style block,
  // both null when no override is active. Kept in one place so the
  // editor preview frame can reuse the exact same logic.
  const pageLayoutCss = buildPageLayoutCss({
    scopeClass: 'rsvp-page-edge-to-edge',
    sectionSelector: '[data-testid="rsvp-section"]',
    edgeToEdge: !!dedicatedSection?.data?.pageEdgeToEdge,
    topSpacing: dedicatedSection?.data?.pageTopSpacing,
    bottomSpacing: dedicatedSection?.data?.pageBottomSpacing,
  });

  return (
    <div
      className={`min-h-screen flex flex-col${pageLayoutCss.wrapperClass ? ' ' + pageLayoutCss.wrapperClass : ''}`}
      style={{ backgroundColor: bg, color: fg, fontFamily: rsvpPage.font_body || 'inherit' }}
      data-testid="public-invite-rsvp-page"
    >
      {/* Page-layout overrides (edge-to-edge + top/bottom spacing) are
          injected as one scoped style block so existing pages stay
          untouched unless the designer opts in via the RSVP Page Editor. */}
      {pageLayoutCss.css && <style>{pageLayoutCss.css}</style>}
      {/* Preview · Draft badge — same affordance as PublicInvite. */}
      {data?.is_preview && <PreviewDraftBadge />}
      {searchParams.get('debug') === 'layout' && <LayoutDebugOverlay />}
      {/* Show version-mismatch banner for designers debugging via
          ?preview= — guests never set this so they never see it. */}
      {previewToken && <NewVersionBanner dirty={false} />}
      {/* Header — "Back to invitation" + event title for context. */}
      <header className="px-5 pt-6 pb-2 flex items-center gap-3 max-w-md mx-auto w-full">
        <button
          onClick={() => navigate(`/invite/${slug}${queryString ? `?${queryString}` : ''}`)}
          className="inline-flex items-center gap-1.5 text-[12px] opacity-70 hover:opacity-100 transition-opacity"
          data-testid="rsvp-back-to-invitation-btn"
          aria-label="Back to invitation"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to invitation
        </button>
      </header>

      {/* Centered RSVP body — when the designer has opened the RSVP
          Page Editor at least once, render the dedicated v2 section
          (Card / Minimal / Soft Card / Luxe Card / Detailed /
          Immersive) using the same RsvpSection registry that powers
          inline RSVP. Otherwise fall back to the legacy minimal form
          so older events keep working.

          Page layout — when the designer turns on "Edge-to-edge
          background" (stored at section.data.pageEdgeToEdge), the
          wrapper drops its horizontal padding and width cap so a
          Background-width=100% surface can actually touch the
          viewport edges on a phone. Default OFF preserves the
          inset look existing pages have today. */}
      {(() => {
        const edgeToEdge = !!(dedicatedSection?.data?.pageEdgeToEdge);
        const mainCls = edgeToEdge
          ? 'flex-1 flex items-start justify-center pb-16'
          : 'flex-1 flex items-start justify-center px-5 pb-16 pt-4';
        const innerCls = edgeToEdge ? 'w-full' : 'w-full max-w-md';
        return (
          <main className={mainCls}>
            <div className={innerCls}>
              {dedicatedSection ? (
                <RsvpSection
                  section={dedicatedSection}
                  data={dedicatedSection.data || {}}
                  theme={{
                    accent,
                    textPrimary: fg,
                    textSecondary: colorTheme.text_secondary || fg,
                    background: bg,
                    bg,
                    eyebrowFont: headingFont,
                    bodyFont: rsvpPage.font_body || 'inherit',
                  }}
                  settings={{ slug, motionPreset: 'none' }}
                />
              ) : (
                <>
                  {event.name && (
                    <div className="text-center mb-8">
                      <p className="text-[11px] uppercase tracking-[0.18em] opacity-60 mb-2">
                        RSVP
                      </p>
                      <h1
                        className="text-[28px] sm:text-[34px] leading-tight"
                        style={{ fontFamily: headingFont, color: fg }}
                      >
                        {event.name}
                      </h1>
                    </div>
                  )}
                  <RSVPForm
                    slug={slug}
                    accentColor={accent}
                    colorTheme={colorTheme}
                    fontStyle={rsvpPage.font_style}
                    labels={rsvpPage.rsvp_labels || rsvpPage.labels || {}}
                    buttonShadowStrength={rsvpPage.button_shadow_strength}
                  />
                </>
              )}
            </div>
          </main>
        );
      })()}
    </div>
  );
}
